import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, FileText, Printer } from 'lucide-react'
import { useLmsPrint } from '@/hooks/useLmsPrint'
import { LmsPdfDownloadButton } from '@/components/admin/print/LmsPdfDownloadButton'
import { Button } from '@quebon/ui/components/button'
import { Badge } from '@quebon/ui/components/badge'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Textarea } from '@quebon/ui/components/textarea'
import { Spinner } from '@quebon/ui/components/spinner'
import { cumulativeAssessmentAdminApi } from '@/api/cumulativeAssessmentAdmin'
import { AdminSelect } from '@/components/admin/common/AdminSelect'
import { AiqBellCurve } from '@/components/admin/diagnostics/AiqBellCurve'
import { CumulativeGrowthChart } from '@/components/admin/cumulativeReport/CumulativeGrowthChart'
import {
  buildCumulativeReportFileName,
  courseDisplayName,
  defaultOverallCommentKey,
  formatCoverDate,
  formatHeaderDate,
  reportStampOf,
  groupChaptersByCourse,
  toAiqReportData,
  withCourseDisplayNames,
} from '@/components/admin/cumulativeReport/cumulativeReportFormat'
import type { CourseNameLabels } from '@/components/admin/cumulativeReport/cumulativeReportFormat'
import type {
  CumulativeAssessmentReportView,
  CumulativeGrowthPointView,
  CumulativeReportBand,
  CumulativeReportChapterView,
  CumulativeReportItemView,
  CumulativeReportMark,
  CumulativeReportTrackCourseView,
} from '@/types/coaching'
import { getApiErrorCode } from '@/utils/apiError'
import { DIAGNOSTIC_A4_PAGE, DIAGNOSTIC_PRINT_PAGE_STYLE, formatDiagnosticGradeLabels } from './StudentDiagnosticReport'
import { StudentAiqReportContent } from './StudentAiqReport'

export { buildCumulativeReportFileName }

const PAGE_STYLE = { width: DIAGNOSTIC_A4_PAGE.widthPx, height: DIAGNOSTIC_A4_PAGE.heightPx } as const
const BAND_COLORS: Record<CumulativeReportBand, string> = { HIGH: '#4599ff', MID: '#ffaa00', LOW: '#ff675e' }
/** Fixed rows per answer-sheet page: twenty fill one A4 page exactly (Figma 16-2942); thirty split 20 + 10. */
const SHEET_ROWS_PER_PAGE = 20

type T = ReturnType<typeof useTranslation>['t']

interface HeaderContext {
  name: string
  gradeShort: string
  date: string
}

/**
 * Cumulative Report (cumulative-report-plan §7, ADR 0012). Staff-only A4 document: cover, summary, growth and
 * three-chapter comparison, answer sheet, and the two Math Head pages, which are omitted entirely below Math Head
 * Norm Sufficiency (D4). Pages are fixed 794×1123px sections printed one per sheet like the diagnostic report;
 * answer-sheet rows are fixed, while free-form overall comments are measured and continued on extra pages.
 */
export default function CumulativeAssessmentReportPage() {
  const { learnerId = '', assessmentId = '' } = useParams()
  const { t, i18n } = useTranslation('admin')
  const learner = Number(learnerId)
  const assessment = Number(assessmentId)
  const printRef = useRef<HTMLDivElement>(null)
  const [commentPageCount, setCommentPageCount] = useState(0)
  const [measuredLayoutKey, setMeasuredLayoutKey] = useState<string | null>(null)
  const [commentPaginationError, setCommentPaginationError] = useState(false)

  const reportQuery = useQuery({
    queryKey: ['lc', 'admin', 'cumulativeReport', learner, assessment],
    queryFn: () => cumulativeAssessmentAdminApi.getReport(learner, assessment),
    retry: false,
  })
  const courseNames: CourseNameLabels = {
    core: (level) => t('cumulativeReport.course.core', { level }),
    advanced: (level) => (level ? t('cumulativeReport.course.advancedLevel', { level }) : t('cumulativeReport.course.advanced')),
    elite: () => t('cumulativeReport.course.elite'),
  }
  const report = reportQuery.data ? withCourseDisplayNames(reportQuery.data, courseNames) : undefined
  const growthQuery = useQuery({
    queryKey: ['lc', 'admin', 'cumulativeReportGrowth', learner, report?.sequence],
    queryFn: () => cumulativeAssessmentAdminApi.getGrowth(learner, report?.sequence ?? 0, 10),
    enabled: report != null,
    retry: false,
  })
  const layoutKey = JSON.stringify([learner, assessment, report?.reportVersion, report?.overallComment, i18n.language])
  const handleCommentPagination = useCallback((count: number | null, failed = false) => {
    if (count == null) setMeasuredLayoutKey(null)
    else {
      setCommentPageCount(count)
      setMeasuredLayoutKey(layoutKey)
    }
    setCommentPaginationError(failed)
  }, [layoutKey])
  const canPrint = !!report && !reportQuery.isFetching && !reportQuery.isError &&
    !growthQuery.isFetching && !growthQuery.isError && measuredLayoutKey === layoutKey
  const { print: reactToPrintFn, downloadPdf, canDownloadPdf, isDownloading, error: pdfError } = useLmsPrint({
    contentRef: printRef,
    canPrint,
    documentTitle: report
      ? buildCumulativeReportFileName(report, t('cumulativeReport.fileRound', { n: report.sequence }), t('cumulativeReport.fileSuffix'))
      : 'cumulative-report',
    pageStyle: DIAGNOSTIC_PRINT_PAGE_STYLE,
  })

  if (reportQuery.isLoading) {
    return (
      <div role="status" className="flex min-h-screen flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        <Spinner aria-hidden="true" className="size-8 motion-reduce:animate-none" />
        <p>{t('cumulativeReport.loading')}</p>
      </div>
    )
  }
  if (reportQuery.isError || !report) {
    const code = getApiErrorCode(reportQuery.error)
    return (
      <p role="status" className="p-8 text-center text-sm text-gray-500">
        {code === 'CUMULATIVE_REPORT_NOT_PUBLISHED' ? t('cumulativeReport.notPublished') : t('cumulativeReport.loadError')}
      </p>
    )
  }

  const grades = formatDiagnosticGradeLabels(
    { gradeLevel: report.learner.gradeLevel ?? null, gradeLabel: report.learner.gradeLabel },
    t,
  )
  const header: HeaderContext = {
    name: report.learner.name,
    gradeShort: grades.short,
    date: formatHeaderDate(reportStampOf(report)),
  }
  const sheetPages = chunk(report.lessonTest?.items ?? [], SHEET_ROWS_PER_PAGE)
  const sheetPageCount = Math.max(sheetPages.length, 1)
  const mathHead = report.mathHead ?? null

  return (
    <div className="min-h-screen bg-gray-100">
      <div
        className="print-action-bar border-b border-border bg-muted/40 px-4 py-6 sm:px-6"
        style={{ '--color-primary': 'var(--primary-color)', '--color-ring': 'var(--primary-color)' } as CSSProperties}
      >
        <div className="mx-auto mb-4 flex justify-end gap-2" style={{ maxWidth: DIAGNOSTIC_A4_PAGE.widthPx }}>
        <Button type="button" onClick={() => reactToPrintFn()}>
          <Printer data-icon="inline-start" />
          {t('cumulativeReport.print')}
        </Button>
        {canDownloadPdf && (
          <LmsPdfDownloadButton onClick={downloadPdf} disabled={!canPrint} isDownloading={isDownloading} error={pdfError ?? (commentPaginationError ? t('common:apiError.PRINT_PDF_ASSET_FAILED') : null)} />
        )}
        </div>
        {!mathHead ? (
          <p data-testid="cumulative-math-head-omitted" className="text-xs text-gray-500">
            {t('cumulativeReport.mathHeadOmitted')}
          </p>
        ) : null}
        <OverallCommentEditor learnerId={learner} assessmentId={assessment} report={report} />
      </div>

      <div className="mx-auto py-8" style={{ maxWidth: DIAGNOSTIC_A4_PAGE.widthPx }}>
        <div
          id="diagnostic-report-print"
          ref={printRef}
          data-testid="cumulative-report-print"
          className="bg-white shadow-lg print:shadow-none"
        >
          <CoverPage report={report} gradeFull={grades.full} locale={i18n.language} />
          <SummaryPage report={report} header={header} onPageCountChange={handleCommentPagination} />
          <GrowthPage report={report} header={header} pageNumber={2 + commentPageCount} growth={(growthQuery.data ?? []).map((point) => (
            point.courseTitle ? { ...point, courseTitle: courseDisplayName(point.courseTitle, courseNames) } : point
          ))} />
          {(sheetPages.length ? sheetPages : [[]]).map((rows, index) => (
            <AnswerSheetPage
              key={rows[0]?.number ?? index}
              rows={rows}
              header={header}
              pageNumber={3 + commentPageCount + index}
              showTitle={index === 0}
              showFootnote={index === sheetPageCount - 1}
              hasReviewRows={(report.lessonTest?.items ?? []).some((item) => item.fromReviewRange)}
            />
          ))}
          {mathHead ? (
            <StudentAiqReportContent
              data={toAiqReportData(report, mathHead)}
              gradeLabel={grades.short}
              showPrintButton={false}
              elapsedMin={mathHead.durationMs == null ? null : Math.round(mathHead.durationMs / 60000)}
              pageStart={3 + commentPageCount + sheetPageCount}
              className="bg-white"
              copy={{
                title: t('cumulativeReport.headerTitle'),
                headerDate: header.date,
                summaryIndexPrefix: t('cumulativeReport.mathHead.indexPrefix', { name: report.learner.name }),
                summaryIndexSuffix: t('cumulativeReport.mathHead.indexSuffix'),
                summaryRankPrefix: t('cumulativeReport.mathHead.rankPrefix'),
                distDesc: t('cumulativeReport.mathHead.distDesc'),
                distCaption: t('cumulativeReport.mathHead.distCaption'),
                infoTitle: t('cumulativeReport.mathHead.infoTitle'),
                infoHeadline: t('cumulativeReport.mathHead.infoHeadline'),
                infoBody1: t('cumulativeReport.mathHead.infoBody1'),
                infoBody2: t('cumulativeReport.mathHead.infoBody2'),
                infoBody3: '',
                infoAppearance: 'cumulative',
                infoCards: {
                  abstraction: {
                    label: t('cumulativeReport.mathHead.cards.abstraction.label'),
                    headline: t('cumulativeReport.mathHead.cards.abstraction.headline'),
                    body: t('cumulativeReport.mathHead.cards.abstraction.body'),
                    emphasis: t('cumulativeReport.mathHead.cards.abstraction.emphasis'),
                  },
                  change: {
                    label: t('cumulativeReport.mathHead.cards.change.label'),
                    headline: t('cumulativeReport.mathHead.cards.change.headline'),
                    body: t('cumulativeReport.mathHead.cards.change.body'),
                    emphasis: t('cumulativeReport.mathHead.cards.change.emphasis'),
                  },
                  relation: {
                    label: t('cumulativeReport.mathHead.cards.relation.label'),
                    headline: t('cumulativeReport.mathHead.cards.relation.headline'),
                    body: t('cumulativeReport.mathHead.cards.relation.body'),
                    emphasis: t('cumulativeReport.mathHead.cards.relation.emphasis'),
                  },
                },
                compareDesc: t('cumulativeReport.mathHead.compareDesc'),
                comparisonFootnote: t('cumulativeReport.mathHead.comparisonFootnote'),
                sameGroupLabel: t('cumulativeReport.mathHead.sameGroup'),
                topGroupLabel: t('cumulativeReport.mathHead.topGroup'),
                sameAveragePrefix: t('cumulativeReport.mathHead.sameAveragePrefix'),
                sameAverageValue: (value) => t('cumulativeReport.mathHead.sameAverageValue', { value }),
                hideItemCount: true,
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

// ── 총평 편집 (화면 전용, 인쇄 제외) ─────────────────────────────────────────
function OverallCommentEditor({
  learnerId,
  assessmentId,
  report,
}: {
  learnerId: number
  assessmentId: number
  report: CumulativeAssessmentReportView
}) {
  const { t } = useTranslation('admin')
  const queryClient = useQueryClient()
  const defaultComment = useDefaultOverallComment(report)
  // With nothing saved the report prints the default, so the editor starts from the text that will be printed.
  const baseline = report.overallComment ?? defaultComment ?? ''
  const [draft, setDraft] = useState(baseline)
  const [notice, setNotice] = useState<string | null>(null)
  const [directInput, setDirectInput] = useState(false)
  const recommendedKey = defaultOverallCommentKey(report.lessonTest?.band, report.mathHead?.band)
  const bands = ['HIGH', 'MID', 'LOW'] as const
  const templates = bands.flatMap((mathHeadBand) => bands.map((lessonBand) => {
    const key = defaultOverallCommentKey(lessonBand, mathHeadBand)!
    const label = t('cumulativeReport.comment.templateLabel', {
      mathHead: t(`diagnosticReport.band.${mathHeadBand}`),
      lesson: t(`diagnosticReport.band.${lessonBand}`),
    })
    return {
      value: key,
      text: t(key),
      label: key === recommendedKey ? `${label} · ${t('cumulativeReport.comment.recommended')}` : label,
    }
  }))
  const selectedTemplate = templates.find((template) => template.text === draft)
  useEffect(() => {
    setDraft(baseline)
    setDirectInput(false)
  }, [baseline, learnerId, assessmentId])

  const save = useMutation({
    mutationFn: (comment: string) => cumulativeAssessmentAdminApi.updateOverallComment(
      learnerId, assessmentId, comment, report.reportVersion,
    ),
    onSuccess: async () => {
      setNotice(t('cumulativeReport.comment.saved'))
      await queryClient.invalidateQueries({ queryKey: ['lc', 'admin', 'cumulativeReport', learnerId, assessmentId] })
    },
    onError: (error) => {
      const code = getApiErrorCode(error)
      setNotice(
        code === 'CUMULATIVE_REPORT_VERSION_CONFLICT' || code === 'CUMULATIVE_REPORT_COMMENT_TOO_LONG'
          ? t(`cumulativeReport.apiError.${code}`)
          : t('cumulativeReport.comment.error'),
      )
    },
  })

  return (
    <fieldset disabled={save.isPending} className="mx-auto w-full min-w-0" style={{ maxWidth: DIAGNOSTIC_A4_PAGE.widthPx }}>
      <Card>
        <CardHeader className="border-b">
          <CardTitle>{t('cumulativeReport.comment.editorTitle')}</CardTitle>
          <CardDescription>{t('cumulativeReport.comment.editorDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-[280px_minmax(0,1fr)]">
            <aside className="flex flex-col gap-3 border-b border-border pb-5 md:border-b-0 md:border-r md:pb-0 md:pr-6" aria-label={t('cumulativeReport.comment.recommended')}>
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <FileText size={16} className="text-primary" aria-hidden="true" />
                {t('cumulativeReport.comment.recommended')}
              </div>
              {defaultComment ? (
                <>
                  <Badge variant="secondary">
                    {t('cumulativeReport.comment.templateLabel', {
                      mathHead: t(`diagnosticReport.band.${report.mathHead?.band}`),
                      lesson: t(`diagnosticReport.band.${report.lessonTest?.band}`),
                    })}
                  </Badge>
                  <p className="whitespace-pre-line text-sm leading-6 text-muted-foreground">{defaultComment}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-auto w-full"
                    disabled={draft === defaultComment}
                    onClick={() => { setDraft(defaultComment); setDirectInput(false); setNotice(null) }}
                  >
                    {draft === defaultComment ? <Check data-icon="inline-start" /> : null}
                    {t(draft === defaultComment ? 'cumulativeReport.comment.recommendationApplied' : 'cumulativeReport.comment.useDefault')}
                  </Button>
                </>
              ) : <p className="text-sm leading-6 text-muted-foreground">{t('cumulativeReport.comment.noRecommendation')}</p>}
            </aside>
            <FieldGroup className="gap-4">
              <Field>
                <FieldLabel>{t('cumulativeReport.comment.selectLabel')}</FieldLabel>
                <AdminSelect
                  ariaLabel={t('cumulativeReport.comment.selectLabel')}
                  className="w-full"
                  value={directInput ? 'custom' : selectedTemplate?.value ?? 'custom'}
                  options={[{ value: 'custom', label: t('cumulativeReport.comment.directInput') }, ...templates]}
                  onValueChange={(value) => {
                    setNotice(null)
                    setDirectInput(value === 'custom')
                    const template = templates.find((item) => item.value === value)
                    if (template) setDraft(template.text)
                  }}
                />
              </Field>
              <Field>
                <div className="flex items-center justify-between gap-2">
                  <FieldLabel htmlFor="cumulative-overall-comment-input">{t('cumulativeReport.comment.contentLabel')}</FieldLabel>
                  <span className="text-xs tabular-nums text-muted-foreground">{draft.length.toLocaleString()} / 2,000</span>
                </div>
                <Textarea
                  id="cumulative-overall-comment-input"
                  aria-label={t('cumulativeReport.comment.editorTitle')}
                  value={draft}
                  maxLength={2000}
                  rows={8}
                  className="min-h-48"
                  onChange={(event) => {
                    setDraft(event.target.value)
                    setDirectInput(true)
                    setNotice(null)
                  }}
                  placeholder={t('cumulativeReport.comment.placeholder')}
                />
                <FieldDescription>{t('cumulativeReport.comment.editHelp')}</FieldDescription>
              </Field>
            </FieldGroup>
          </div>
        </CardContent>
        <CardFooter className="flex-wrap justify-between gap-3">
          <span role="status" className="text-xs text-muted-foreground">
            {notice ?? t(draft === baseline ? 'cumulativeReport.comment.upToDate' : 'cumulativeReport.comment.unsaved')}
          </span>
          <Button
            type="button"
            onClick={() => { setNotice(null); save.mutate(draft) }}
            disabled={save.isPending || draft === baseline}
          >
            {save.isPending ? <Spinner data-icon="inline-start" /> : <Check data-icon="inline-start" />}
            {save.isPending ? t('cumulativeReport.comment.saving') : t('cumulativeReport.comment.save')}
          </Button>
        </CardFooter>
      </Card>
    </fieldset>
  )
}

/** Default Cumulative Overall Comment for the report's Math Head × Lesson Test bands, or null without both. */
function useDefaultOverallComment(report: CumulativeAssessmentReportView): string | null {
  const { t } = useTranslation('admin')
  const key = defaultOverallCommentKey(report.lessonTest?.band, report.mathHead?.band)
  return key ? t(key) : null
}

// ── 공통 머리글 ──────────────────────────────────────────────────────────────
function ReportHeader({ header }: { header: HeaderContext }) {
  const { t } = useTranslation('admin')
  return (
    // Figma header is 56px with the bottom border inside it: 24px row + 31px + 1px border.
    <header className="flex items-center justify-between border-b border-[#d0d0d0] pb-[31px]">
      <div className="flex items-center gap-3 px-2">
        <img src="/report/diagnostic/logo_kor.svg" alt="" aria-hidden="true" className="h-6 w-[119px]" />
        <span className="h-3.5 w-px bg-[#d0d0d0]" aria-hidden="true" />
        <h1 className="text-base font-bold leading-[19px] text-[#131313]">{t('cumulativeReport.headerTitle')}</h1>
      </div>
      <div className="flex items-center gap-4 px-2 text-sm text-[#333]">
        <div className="flex items-center gap-2">
          <HeaderIcon src="/report/diagnostic/icon-user.svg" />
          <span className="flex items-center gap-1">
            <span className="font-medium leading-[21px]">{header.name}</span>
            <span className="text-xs leading-[18px] text-[#4599ff]">({header.gradeShort})</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <HeaderIcon src="/report/diagnostic/icon-calendar.svg" />
          <span className="font-medium leading-[21px]">{header.date}</span>
        </div>
      </div>
    </header>
  )
}

function HeaderIcon({ src }: { src: string }) {
  return (
    <span className="flex size-6 items-center justify-center rounded-full bg-[#eee]">
      <img src={src} alt="" aria-hidden="true" className="size-3.5" />
    </span>
  )
}

function Page({ testId, children, className = '' }: { testId: string; children: ReactNode; className?: string }) {
  return (
    <section
      data-testid={testId}
      className={`diagnostic-report-page relative flex break-before-page flex-col bg-white px-12 pb-12 pt-10 ${className}`}
      style={PAGE_STYLE}
    >
      {children}
    </section>
  )
}

function PageNumber({ value }: { value: number }) {
  return (
    <div className="absolute bottom-4 left-12 flex w-[698px] justify-center px-2 text-sm font-medium leading-[21px] text-[#333]">
      {value}
    </div>
  )
}

function SectionTitle({ children, flush = false }: { children: ReactNode; flush?: boolean }) {
  return (
    <h2 className={`${flush ? '' : 'px-2 '}text-base font-bold leading-[1.2] tracking-[-0.6px] text-[#131313]`}>{children}</h2>
  )
}

// ── 표지 ─────────────────────────────────────────────────────────────────────
function CoverPage({
  report,
  gradeFull,
  locale,
}: {
  report: CumulativeAssessmentReportView
  gradeFull: string
  locale: string
}) {
  const { t } = useTranslation('admin')
  return (
    <section
      data-testid="cumulative-report-cover"
      className="diagnostic-report-page flex flex-col items-center justify-between bg-white px-14 pb-12 pt-[104px] text-center"
      style={PAGE_STYLE}
    >
      {/* Figma 16:4163 — container (gap 40) · info (pt 40) · footer, spread by justify-between. */}
      <div className="flex flex-col items-center gap-10">
        <div className="flex items-center justify-center gap-6 text-2xl font-bold leading-[1.2] tracking-[-0.6px] text-[#888]">
          <span className="h-5 w-[1.5px] bg-[#d0d0d0]" aria-hidden="true" />
          <span>{t('cumulativeReport.cover.brandLine')}</span>
          <span className="h-5 w-[1.5px] bg-[#d0d0d0]" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-2 font-extrabold">
          <p className="text-[46px] leading-[1.2] text-[#131313]">{t('cumulativeReport.cover.titleLine1')}</p>
          <p className="text-[56px] leading-[1.2] tracking-[-1.5px] text-[#4599ff]">
            {t('cumulativeReport.cover.titleLine2Prefix')}
            <span className="text-[#23b8bc]">{t('cumulativeReport.cover.titleLine2Accent')}</span>
          </p>
        </div>
        <div className="text-base leading-[1.5] text-[#131313]">
          <p className="font-bold">{t('cumulativeReport.cover.descriptionStrong')}</p>
          <p>{t('cumulativeReport.cover.descriptionLine2')}</p>
        </div>
        <CoverRings
          lesson={{
            icon: LESSON_ICON_32,
            accent: t('cumulativeReport.cover.lessonAccent'),
            suffix: t('cumulativeReport.cover.testSuffix'),
            caption: t('cumulativeReport.cover.lessonCaption'),
          }}
          mathHead={{
            icon: MATH_HEAD_ICON_32,
            accent: t('cumulativeReport.cover.mathHeadAccent'),
            suffix: t('cumulativeReport.cover.testSuffix'),
            caption: t('cumulativeReport.cover.mathHeadCaption'),
          }}
        />
      </div>

      <div className="flex w-[489px] flex-col items-start gap-4 pt-10 text-left" data-testid="cumulative-cover-legend">
        <CoverLegendRow icon={LESSON_ICON_24} color="#4599ff"
          label={t('cumulativeReport.cover.lessonLegend')} description={t('cumulativeReport.cover.lessonLegendDesc')} />
        <CoverLegendRow icon={MATH_HEAD_ICON_24} color="#23b8bc"
          label={t('cumulativeReport.cover.mathHeadLegend')} description={t('cumulativeReport.cover.mathHeadLegendDesc')} />
      </div>

      <div className="flex h-[113px] w-full max-w-[663px] items-center justify-between rounded-lg bg-[#f5f5f5] px-8 py-5">
        <img src="/report/diagnostic/logo_kor.svg" alt={t('cumulativeReport.cover.logoAlt')} className="h-6 w-[119px]" />
        <dl className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-2 gap-y-1.5 text-left text-sm leading-[1.2]">
          <dt className="tracking-[-0.6px] text-[#888]">{t('cumulativeReport.cover.name')}</dt>
          <dd className="tracking-[-0.6px] text-[#131313]">{report.learner.name}</dd>
          <dt className="tracking-[-0.6px] text-[#888]">{t('cumulativeReport.cover.grade')}</dt>
          <dd className="whitespace-nowrap tracking-[-0.6px] text-[#131313]">{gradeFull}</dd>
          <dt className="tracking-[-0.6px] text-[#888]">{t('cumulativeReport.cover.completedAt')}</dt>
          <dd className="whitespace-nowrap text-[#131313]">{formatCoverDate(reportStampOf(report), locale)}</dd>
        </dl>
      </div>
    </section>
  )
}

// Figma exports: 32px in the rings, 24px in the legend.
const LESSON_ICON_32 = '/report/cumulative/icon-board-lesson-32.svg'
const LESSON_ICON_24 = '/report/cumulative/icon-board-lesson-24.svg'
const MATH_HEAD_ICON_32 = '/report/cumulative/icon-bulb-math-head-32.svg'
const MATH_HEAD_ICON_24 = '/report/cumulative/icon-bulb-math-head-24.svg'

function CoverLegendRow({ icon, color, label, description }: { icon: string; color: string; label: string; description: string }) {
  return (
    <div className="flex items-center gap-2">
      <img src={icon} alt="" className="size-6" />
      <p className="flex items-center gap-4 whitespace-nowrap text-sm leading-[1.2] tracking-[-0.6px]">
        <span className="font-bold" style={{ color }}>{label}</span>
        <span className="text-[#131313]">{description}</span>
      </p>
    </div>
  )
}

/** Cover ring geometry from Figma 16:4173: two 240px circles, 28px border, overlapping by one border width. */
const COVER_RING = { radius: 120, band: 28, centerGap: 212 }

interface CoverRingLabel {
  icon: string
  accent: string
  suffix: string
  caption: string
}

/**
 * The two cover rings drawn as one SVG so their overlap is an exact lens: the part where the two outer disks meet
 * is filled solid and carries the "+" (Figma), instead of one ring painting over the other.
 */
function CoverRings({ lesson, mathHead }: { lesson: CoverRingLabel; mathHead: CoverRingLabel }) {
  const { radius, band, centerGap } = COVER_RING
  const width = centerGap + radius * 2
  const height = radius * 2
  const leftX = radius
  const rightX = radius + centerGap
  const plusX = (leftX + rightX) / 2
  const ringRadius = radius - band / 2
  return (
    // Figma contents frame: pt 100 on top of the container's 40px gap.
    <div className="relative mt-[100px]" style={{ width, height }} aria-hidden="true" data-testid="cumulative-cover-rings">
      <svg className="absolute inset-0" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <clipPath id="cumulative-cover-lesson-disk">
            <circle cx={leftX} cy={radius} r={radius} />
          </clipPath>
        </defs>
        <circle cx={leftX} cy={radius} r={ringRadius} fill="none" stroke="#a2ccff" strokeWidth={band} />
        <circle cx={rightX} cy={radius} r={ringRadius} fill="none" stroke="#91dbdd" strokeWidth={band} />
        <circle cx={rightX} cy={radius} r={radius} fill="#0098cf" clipPath="url(#cumulative-cover-lesson-disk)" />
        <path
          d={`M${plusX} ${radius - 6}v12M${plusX - 6} ${radius}h12`}
          stroke="#fff"
          strokeWidth={3}
          strokeLinecap="round"
        />
      </svg>
      <CoverRingText label={lesson} accentColor="#4599ff" centerX={leftX} />
      <CoverRingText label={mathHead} accentColor="#23b8bc" centerX={rightX} />
    </div>
  )
}

function CoverRingText({ label, accentColor, centerX }: { label: CoverRingLabel; accentColor: string; centerX: number }) {
  const inner = (COVER_RING.radius - COVER_RING.band) * 2
  return (
    <div
      className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[19.2px]"
      style={{ left: centerX, width: inner }}
    >
      <img src={label.icon} alt="" className="size-8" />
      <div className="flex flex-col items-center gap-[6.859px] whitespace-nowrap">
        <p className="text-xl font-semibold leading-[24px] text-[#131313]">
          <span style={{ color: accentColor }}>{label.accent}</span>{label.suffix}
        </p>
        <p className="text-xs leading-[15px] text-[#888]">{label.caption}</p>
      </div>
    </div>
  )
}

// ── 1페이지 종합 ─────────────────────────────────────────────────────────────
function RangeLines({ chapters, t }: { chapters: CumulativeReportChapterView[]; t: T }) {
  return (
    <>
      {groupChaptersByCourse(chapters).map((group) => {
        const first = group.ordinals[0]
        const last = group.ordinals[group.ordinals.length - 1]
        const range = first === last
          ? t('cumulativeReport.summary.chapterSingle', { chapter: first })
          : t('cumulativeReport.summary.chapterRange', { start: first, end: last })
        return (
          <p key={`${group.courseTitle}-${first}`} className="text-[13px] font-medium leading-[18px] text-[#888]">
            {[group.courseTitle, range].filter(Boolean).join(' ')}
          </p>
        )
      })}
    </>
  )
}

function SummaryPage({ report, header, onPageCountChange }: {
  report: CumulativeAssessmentReportView
  header: HeaderContext
  onPageCountChange: (count: number | null, failed?: boolean) => void
}) {
  const { t } = useTranslation('admin')
  const lessonTest = report.lessonTest ?? null
  const mathHead = report.mathHead ?? null
  const hasReview = report.reviewRange.length > 0
  const defaultComment = useDefaultOverallComment(report)
  const comment = report.overallComment?.trim() || defaultComment
  const slotRef = useRef<HTMLDivElement>(null)
  const commentRef = useRef<HTMLElement>(null)
  const [continuations, setContinuations] = useState<string[]>([])
  const layoutKey = JSON.stringify([report.scope, report.reviewRange, report.courseTrack, header])

  useLayoutEffect(() => {
    const slot = slotRef.current
    if (!slot || !comment) {
      setContinuations((previous) => previous.length ? [] : previous)
      onPageCountChange(0)
      return
    }
    const paper = slot.closest<HTMLElement>('.diagnostic-report-page')!
    const measure = () => {
      const source = commentRef.current
      if (!source || slot.clientWidth === 0) return
      // Measure a detached-from-flow copy at the exact printed width; never truncate stored prose.
      const probe = source.cloneNode(true) as HTMLElement
      probe.removeAttribute('data-testid')
      Object.assign(probe.style, {
        position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
        width: `${slot.clientWidth}px`, top: '0', left: '0',
      })
      slot.appendChild(probe)
      const text = probe.querySelector('p')!
      const measureText = (value: string) => {
        text.textContent = value
        return probe.getBoundingClientRect().height
      }
      try {
        const style = getComputedStyle(paper)
        const bottom = paper.getBoundingClientRect().bottom - parseFloat(style.paddingBottom)
        const next: string[] = []
        if (measureText(comment) > bottom - slot.getBoundingClientRect().top) {
          const headerHeight = paper.querySelector('header')!.getBoundingClientRect().height
          const capacity = paper.clientHeight - parseFloat(style.paddingTop)
            - parseFloat(style.paddingBottom) - headerHeight - parseFloat(style.rowGap)
          const characters = Array.from(comment)
          let start = 0
          while (start < characters.length) {
            let low = 1
            let high = characters.length - start
            // Find the largest prefix that fits, including title, gap and comment padding.
            while (low < high) {
              const middle = Math.ceil((low + high) / 2)
              if (measureText(characters.slice(start, start + middle).join('')) <= capacity) low = middle
              else high = middle - 1
            }
            next.push(characters.slice(start, start + low).join(''))
            start += low
          }
        }
        setContinuations((previous) => (
          previous.length === next.length && previous.every((value, index) => value === next[index]) ? previous : next
        ))
        onPageCountChange(next.length)
      } finally {
        probe.remove()
      }
    }
    // The prepared print iframe cannot rerun React pagination. Measure only after the live
    // page's fonts settle, and invalidate readiness if a later font or layout change starts.
    const fonts = document.fonts
    let active = true
    let generation = 0
    let fontTimeout: ReturnType<typeof setTimeout> | undefined
    const measureAfterFonts = () => {
      const current = ++generation
      if (fontTimeout) clearTimeout(fontTimeout)
      onPageCountChange(null)
      if (!fonts || fonts.status !== 'loading') {
        measure()
        return
      }
      fontTimeout = setTimeout(() => {
        if (active && current === generation) onPageCountChange(null, true)
      }, 15_000)
      void fonts.ready.then(() => {
        if (!active || current !== generation) return
        if (fontTimeout) clearTimeout(fontTimeout)
        measure()
      }, () => {
        if (!active || current !== generation) return
        if (fontTimeout) clearTimeout(fontTimeout)
        onPageCountChange(null, true)
      })
    }
    measureAfterFonts()
    const observer = new ResizeObserver(measureAfterFonts)
    Array.from(paper.children).forEach((child) => { if (child !== slot) observer.observe(child) })
    fonts?.addEventListener('loading', measureAfterFonts)
    return () => {
      active = false
      generation++
      if (fontTimeout) clearTimeout(fontTimeout)
      observer.disconnect()
      fonts?.removeEventListener('loading', measureAfterFonts)
    }
  }, [comment, layoutKey, t, onPageCountChange])

  return (
    <>
      <Page testId="cumulative-summary" className="gap-7 [&>*]:shrink-0">
        <ReportHeader header={header} />
        <p className="px-2 text-[22px] font-bold leading-[1.3] tracking-[-0.6px] text-[#131313]">
          {t('cumulativeReport.summary.headlinePrefix', { name: report.learner.name })}
          <span className="text-[#4599ff]">{t('cumulativeReport.summary.headlineRound', { sequence: report.sequence })}</span>
          {t('cumulativeReport.summary.headlineSuffix')}
        </p>

        <section className="flex flex-col">
          <div className={hasReview ? 'grid grid-cols-2 gap-4 px-2' : 'px-2'}>
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-bold leading-[19px] text-[#131313]">{t('cumulativeReport.summary.scopeTitle')}</h2>
              <RangeLines chapters={report.scope} t={t} />
            </div>
            {hasReview ? (
              <div className="flex flex-col gap-1" data-testid="cumulative-review-range">
                <h2 className="text-base font-bold leading-[19px] text-[#131313]">{t('cumulativeReport.summary.reviewTitle')}</h2>
                <RangeLines chapters={report.reviewRange} t={t} />
              </div>
            ) : null}
          </div>
          <CourseTrack track={report.courseTrack ?? []} scope={report.scope} review={report.reviewRange} />
        </section>

        <section className="grid grid-cols-2 gap-2">
          <ResultCard
            title={t('cumulativeReport.summary.lessonResultTitle')}
            icon="/report/diagnostic/icon-board.svg"
            value={lessonTest?.score ?? null}
            suffix={t('cumulativeReport.summary.outOf100')}
            band={lessonTest?.band ?? null}
            visual={lessonTest?.score != null ? <ScoreRing score={lessonTest.score} band={lessonTest.band ?? 'LOW'} /> : null}
          />
          <ResultCard
            title={t('cumulativeReport.summary.mathHeadResultTitle')}
            icon="/report/diagnostic/icon-bulb.svg"
            value={mathHead?.index ?? null}
            suffix={mathHead
              ? t('cumulativeReport.summary.topPercent', { percent: mathHead.percentile })
              : t('cumulativeReport.summary.mathHeadPendingValue')}
            band={mathHead?.band ?? null}
            visual={mathHead ? <div className="h-[98px] w-[242px]"><AiqBellCurve score={mathHead.index} variant="compact" /></div> : null}
          />
        </section>

        <div ref={slotRef} className="relative">
          {comment && continuations.length === 0 ? <OverallComment text={comment} elementRef={commentRef} /> : null}
        </div>
        <PageNumber value={1} />
      </Page>
      {continuations.map((text, index) => (
        <Page key={index} testId="cumulative-comment-page" className="gap-7 [&>*]:shrink-0">
          <ReportHeader header={header} />
          <OverallComment text={text} elementRef={index === 0 ? commentRef : undefined} />
          <PageNumber value={2 + index} />
        </Page>
      ))}
    </>
  )
}

function OverallComment({ text, elementRef }: { text: string; elementRef?: React.Ref<HTMLElement> }) {
  const { t } = useTranslation('admin')
  return (
    <section ref={elementRef} className="flex flex-col gap-4" data-testid="cumulative-overall-comment">
      <SectionTitle>{t('cumulativeReport.summary.commentTitle')}</SectionTitle>
      <p className="whitespace-pre-wrap [overflow-wrap:anywhere] rounded-xl bg-[#edf5ff] px-5 py-4 text-[13px] leading-[1.6] text-[#131313]">
        {text}
      </p>
    </section>
  )
}

/**
 * D15: every course of the curriculum as a cell sized by its chapter count. Inside a cell the test range (and, from
 * the second round on, the review range) is shaded at its chapter position, and ▼ sits on the end of the test-range band,
 * so a parent sees where this round sits in the whole program.
 */
function CourseTrack({
  track,
  scope,
  review,
}: {
  track: CumulativeReportTrackCourseView[]
  scope: CumulativeReportChapterView[]
  review: CumulativeReportChapterView[]
}) {
  const { t } = useTranslation('admin')
  if (track.length === 0) return null
  const scopeOrdinals = new Set(scope.map((chapter) => chapter.chapterOrdinal))
  const reviewOrdinals = [...new Set(review.map((chapter) => chapter.chapterOrdinal))].filter((ordinal) => !scopeOrdinals.has(ordinal))
  const sortedScope = [...scopeOrdinals].sort((a, b) => a - b)
  const markerOrdinal = sortedScope.length ? sortedScope[sortedScope.length - 1] : null

  return (
    // Figma 16:2753: picker 22px under the range title, 4px above the 40px bars; bars 4px apart, full width.
    <div className="mt-[22px] flex gap-1" aria-label={t('cumulativeReport.summary.trackAria')} data-testid="cumulative-course-track">
      {track.map((course) => {
        const count = course.lastChapterOrdinal - course.firstChapterOrdinal + 1
        const offset = (ordinal: number) => ((ordinal - course.firstChapterOrdinal) / count) * 100
        const inCourse = (ordinal: number) => ordinal >= course.firstChapterOrdinal && ordinal <= course.lastChapterOrdinal
        const marker = markerOrdinal != null && inCourse(markerOrdinal) ? offset(markerOrdinal + 1) : null
        return (
          <div key={`${course.firstChapterOrdinal}-${course.title}`} className="flex min-w-0 flex-col gap-1" style={{ flexGrow: count, flexBasis: 0 }}>
            <div className="relative h-1.5">
              {marker != null ? <Picker className="absolute top-0 -translate-x-1/2" style={{ left: `${marker}%` }} /> : null}
            </div>
            <div className="relative h-10 overflow-hidden rounded-lg bg-[#f5f5f5]">
              <CourseLabel title={course.title} className="text-[#888]" />
              {chapterRuns(reviewOrdinals.filter(inCourse)).map(([from, to]) => (
                <ShadedRun key={`review-${from}`} left={offset(from)} right={100 - offset(to + 1)} color="#c3d8f2" title={course.title} labelClassName="text-[#4f6f96]" />
              ))}
              {chapterRuns(sortedScope.filter(inCourse)).map(([from, to]) => (
                <ShadedRun key={`scope-${from}`} left={offset(from)} right={100 - offset(to + 1)} color="#4599ff" title={course.title} labelClassName="text-white" />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Figma "picker": the 8×6 down-pointing marker above the track and the active band chip. */
function Picker({ className = '', style }: { className?: string; style?: CSSProperties }) {
  return (
    <span className={`flex h-1.5 w-2 items-center justify-center ${className}`} style={style} aria-hidden="true" data-testid="cumulative-picker">
      <img src="/report/cumulative/picker.svg" alt="" className="h-[5.6px] w-[7.13px] rotate-180" />
    </span>
  )
}

/** Consecutive chapter ordinals as [first, last] runs, so a range is painted as one band without seams. */
function chapterRuns(ordinals: number[]): Array<[number, number]> {
  const runs: Array<[number, number]> = []
  ;[...ordinals].sort((a, b) => a - b).forEach((ordinal) => {
    const last = runs[runs.length - 1]
    if (last && ordinal === last[1] + 1) last[1] = ordinal
    else runs.push([ordinal, ordinal])
  })
  return runs
}

function CourseLabel({ title, className }: { title: string; className: string }) {
  return (
    <span className={`absolute inset-0 flex items-center justify-center truncate px-2 text-sm font-bold leading-[1.4] ${className}`}>
      {title}
    </span>
  )
}

/**
 * One shaded run plus a copy of the course label clipped to the same run, so the part of the label that sits on
 * the band switches to a readable color instead of disappearing into it.
 */
function ShadedRun({
  left,
  right,
  color,
  title,
  labelClassName,
}: {
  left: number
  right: number
  color: string
  title: string
  labelClassName: string
}) {
  const clip = `inset(0 ${right}% 0 ${left}%)`
  return (
    <span className="absolute inset-0" style={{ clipPath: clip }} aria-hidden="true" data-testid="cumulative-track-shade">
      <span className="absolute inset-0" style={{ backgroundColor: color }} />
      <CourseLabel title={title} className={labelClassName} />
    </span>
  )
}

function ResultCard({
  title,
  icon,
  value,
  suffix,
  band,
  visual,
}: {
  title: string
  icon: string
  value: number | null
  suffix: string
  band: CumulativeReportBand | null
  visual: ReactNode
}) {
  return (
    // Figma 16:2769: title 17 · graph 152 · state 60, 8px apart inside 24px padding.
    <div className="flex h-[293px] flex-col items-center gap-2 overflow-hidden rounded-2xl bg-[#f5f5f5] p-6">
      <div className="flex items-center gap-1 text-xs font-semibold leading-[1.4] text-[#888]">
        <img src={icon} alt="" className="size-3.5" aria-hidden="true" />
        {title}
      </div>
      <div className="flex h-[152px] w-full flex-col items-center justify-center gap-4">
        <div className="flex items-center gap-1 leading-[1.2]">
          <span className="text-[32px] font-bold text-[#131313]">{value ?? '—'}</span>
          <span className="text-lg font-medium leading-[1.2] text-[#888]">{suffix}</span>
        </div>
        {visual}
      </div>
      <BandChips active={band} />
    </div>
  )
}

function ScoreRing({ score, band }: { score: number; band: CumulativeReportBand }) {
  const color = BAND_COLORS[band]
  return (
    <div
      className="grid size-24 place-items-center rounded-full"
      style={{ background: `conic-gradient(${color} ${Math.min(Math.max(score, 0), 100)}%, #e0e0e6 0)` }}
    >
      {/* Figma ring: 96px with a 14.4px band. */}
      <div className="grid size-[67.2px] place-items-center rounded-full bg-[#f5f5f5]">
        <span className="text-xl font-bold leading-[24px]" style={{ color }}>{score}</span>
      </div>
    </div>
  )
}

function BandChips({ active }: { active: CumulativeReportBand | null }) {
  const { t } = useTranslation('admin')
  return (
    // Figma container-state: 60px tall, chips 28px from its top with the picker directly above the active chip.
    <div className="relative h-[60px] w-full">
      <div className="absolute left-1/2 top-7 grid h-8 w-60 -translate-x-1/2 grid-cols-3 gap-2">
        {(['HIGH', 'MID', 'LOW'] as const).map((band) => (
          <div
            key={band}
            className="relative flex items-center justify-center rounded-lg text-sm font-bold leading-[1.4] text-white"
            style={{ backgroundColor: active === band ? BAND_COLORS[band] : '#d0d0d0' }}
            data-testid={active === band ? 'cumulative-band-active' : undefined}
          >
            {active === band ? <Picker className="absolute -top-1.5 left-1/2 -translate-x-1/2" /> : null}
            {t(`diagnosticReport.band.${band}`)}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── 2페이지 성장 · 비교 ──────────────────────────────────────────────────────
function GrowthPage({
  report,
  header,
  growth,
  pageNumber,
}: {
  report: CumulativeAssessmentReportView
  header: HeaderContext
  pageNumber: number
  growth: CumulativeGrowthPointView[]
}) {
  const { t } = useTranslation('admin')
  const roundLabel = (n: number) => t('cumulativeReport.growth.round', { n })
  // Growth points frozen under another BIGGO scoring policy are not on this report's scale (ADR 0009, 2026-09-22).
  const policyVersion = report.mathHead?.policyVersion ?? null
  const comparable = (point: CumulativeGrowthPointView) =>
    policyVersion == null || (point.mathHeadPolicyVersion ?? null) === policyVersion
  const differentNorms = growth.some((point) => point.mathHeadIndex != null && !comparable(point))
  const { chapterTestAverage, ownScopeScore } = report.comparison
  const score = (value: number | null | undefined) =>
    value == null ? t('cumulativeReport.comparison.noScore') : t('cumulativeReport.comparison.score', { score: value })

  return (
    <Page testId="cumulative-growth" className="gap-7">
      <ReportHeader header={header} />
      <section className="flex flex-col gap-4">
      <SectionTitle>{t('cumulativeReport.growth.title', { name: report.learner.name })}</SectionTitle>
      {[
        { key: 'lesson', title: t('cumulativeReport.growth.lessonTest'), color: '#4599ff', domain: [0, 100] as [number, number],
          value: (point: CumulativeGrowthPointView) => point.lessonTestScore },
        { key: 'mathHead', title: t('cumulativeReport.growth.mathHead'), color: '#23b8bc', domain: [55, 145] as [number, number],
          value: (point: CumulativeGrowthPointView) => (comparable(point) ? point.mathHeadIndex : null) },
      ].map((series) => (
        <div key={series.key} className="flex flex-col gap-4 rounded-xl bg-[#f5f5f5] px-5 pb-6 pt-5">
          <h3 className="text-sm font-bold leading-[1.5] tracking-[-0.6px] text-[#131313]">{series.title}</h3>
          <CumulativeGrowthChart
            points={growth}
            value={series.value}
            domain={series.domain}
            color={series.color}
            roundLabel={roundLabel}
            testId={`cumulative-growth-${series.key}`}
          />
        </div>
      ))}
      {differentNorms ? <p className="text-xs text-gray-600">{t('cumulativeReport.mathHead.growthNote')}</p> : null}
      </section>

      {/* Figma 16:2916: heading group (title · chapters · item count) 24px above the cards and footnote. */}
      <section className="flex flex-col gap-6" data-testid="cumulative-comparison">
        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-col gap-2 px-2">
            <SectionTitle flush>{t('cumulativeReport.comparison.title')}</SectionTitle>
            <p className="flex flex-wrap items-center gap-2 text-xs leading-[14px]">
              <span className="font-medium text-[#4599ff]">{report.scope[0]?.courseTitle}</span>
              {report.scope.map((chapter, index) => (
                <span key={chapter.chapterOrdinal} className="flex items-center gap-2">
                  {index > 0 ? <span className="h-2.5 w-px bg-[#d0d0d0]" aria-hidden="true" /> : null}
                  <span className="flex gap-1">
                    <span className="font-medium text-[#888]">{t('cumulativeReport.comparison.chapterLabel', { chapter: chapter.chapterNumberInCourse ?? chapter.chapterOrdinal })}</span>
                    <span className="text-[#333]">{chapter.chapterTitle}</span>
                  </span>
                </span>
              ))}
            </p>
          </div>
          <span className="rounded-full bg-[#eee] px-2.5 py-1 text-sm font-medium leading-[21px] text-[#333]">
            {t('cumulativeReport.comparison.itemCount', { count: report.lessonTest?.itemCount ?? 0 })}
          </span>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-2xl bg-[#f5f5f5] px-6 py-5">
              <span className="text-[13px] font-bold leading-4 text-[#767676]">{t('cumulativeReport.comparison.chapterTest')}</span>
              <span className="text-[36px] font-extrabold leading-[43px] text-[#333]">{score(chapterTestAverage)}</span>
            </div>
            <img
              src="/report/cumulative/icon-compare-arrow.svg"
              alt=""
              className="h-[35px] w-[30px] shrink-0"
              aria-hidden="true"
              data-testid="cumulative-comparison-arrow"
            />
            <div className="flex flex-1 flex-col items-center justify-center gap-4 self-stretch rounded-xl bg-[#eef5ff] px-4 py-5 text-[#4599ff]">
              <span className="text-[13px] font-bold leading-4">{t('cumulativeReport.comparison.cumulativeTest')}</span>
              <span className="text-[36px] font-extrabold leading-[43px]">{score(ownScopeScore)}</span>
            </div>
          </div>
          <div className="text-[13px] font-medium leading-[18px] text-[#888]">
            <p>{t('cumulativeReport.comparison.chapterTestNote')}</p>
            <p>{t('cumulativeReport.comparison.cumulativeTestNote')}</p>
          </div>
        </div>
      </section>
      <PageNumber value={pageNumber} />
    </Page>
  )
}

// ── 3페이지 정오답표 ─────────────────────────────────────────────────────────
function AnswerSheetPage({
  rows,
  header,
  pageNumber,
  showTitle,
  showFootnote,
  hasReviewRows,
}: {
  rows: CumulativeReportItemView[]
  header: HeaderContext
  pageNumber: number
  showTitle: boolean
  showFootnote: boolean
  hasReviewRows: boolean
}) {
  const { t } = useTranslation('admin')
  return (
    <Page testId="cumulative-answer-sheet-page" className="gap-7">
      <ReportHeader header={header} />
      {/* Figma 16:2971: title, table and footnote 16px apart; rows are flex like the design, not a fixed-width table. */}
      <section className="flex flex-col gap-4">
        {showTitle ? <SectionTitle>{t('cumulativeReport.sheet.title')}</SectionTitle> : null}
        {rows.length === 0 ? (
          <p className="px-2 text-xs text-gray-400">{t('cumulativeReport.sheet.empty')}</p>
        ) : (
          <div
            role="table"
            data-testid="cumulative-answer-sheet"
            className="flex w-full flex-col overflow-hidden rounded-xl border border-[#eaeaea] text-center text-xs"
          >
            <div role="row" className="flex h-10 items-center gap-2.5 bg-[#f5f5f5] pl-4 pr-6 font-medium leading-[14px] text-[#888]">
              <span role="columnheader" className="w-12 shrink-0">{t('cumulativeReport.sheet.no')}</span>
              <span role="columnheader" className="w-14 shrink-0 whitespace-nowrap">{t('cumulativeReport.sheet.firstProgress')}</span>
              <span className="size-2.5 shrink-0" aria-hidden="true" />
              <span role="columnheader" className="w-[92px] shrink-0">{t('cumulativeReport.sheet.graded')}</span>
              <span role="columnheader" className="min-w-0 flex-1">{t('cumulativeReport.sheet.chapter')}</span>
              <span role="columnheader" className="min-w-0 flex-1">{t('cumulativeReport.sheet.lesson')}</span>
            </div>
            {rows.map((row, index) => (
              <div
                role="row"
                key={row.number}
                data-testid="cumulative-answer-row"
                className={`flex h-[42px] items-center gap-2.5 border-b border-[#f5f5f5] pl-4 pr-6 ${row.fromReviewRange ? 'bg-[#f3f8ff]' : index % 2 === 1 ? 'bg-[#f5f5f5]' : 'bg-white'}`}
              >
                <span role="cell" className="flex w-12 shrink-0 items-center justify-center gap-1 font-semibold leading-[14px] text-[#888]">
                  {row.number}
                  {row.fromReviewRange ? (
                    <span data-testid="cumulative-review-tag" className="rounded bg-[#c3d8f2] px-1 text-[9px] font-bold text-[#31418f]">
                      {t('cumulativeReport.sheet.reviewTag')}
                    </span>
                  ) : null}
                </span>
                <span role="cell" className="flex w-14 shrink-0 justify-center"><Mark value={row.firstProgress ?? null} testId="mark-first-progress" /></span>
                <img src="/report/cumulative/arrow-right.svg" alt="" aria-hidden="true" className="size-2.5 shrink-0" />
                <span role="cell" className="flex w-[92px] shrink-0 justify-center"><Mark value={row.graded ?? null} testId="mark-graded" /></span>
                <span role="cell" className="min-w-0 flex-1 truncate leading-[1.4] text-[#333]">{row.chapterTitle ?? '—'}</span>
                <span role="cell" className="min-w-0 flex-1 truncate font-semibold leading-[14px] text-[#131313]">{row.lessonTitle ?? '—'}</span>
              </div>
            ))}
          </div>
        )}
        {showFootnote ? (
          <div className="text-[13px] font-medium leading-[18px] text-[#888]">
            <p>{t('cumulativeReport.sheet.footnote')}</p>
            {hasReviewRows ? <p>{t('cumulativeReport.sheet.reviewFootnote')}</p> : null}
          </div>
        ) : null}
      </section>
      <PageNumber value={pageNumber} />
    </Page>
  )
}

/** First-Progress Outcome and grading are O, X or `−` (no record, D5). */
function Mark({ value, testId }: { value: CumulativeReportMark | null; testId: string }) {
  const { t } = useTranslation('admin')
  if (value == null) {
    return (
      <span data-testid={testId} className="mx-auto block text-base font-bold text-[#888]" title={t('cumulativeReport.sheet.noRecord')}>
        −
      </span>
    )
  }
  return (
    <span data-testid={testId} className="mx-auto block size-5">
      <img src={value === 'O' ? '/report/diagnostic/icon-O.svg' : '/report/diagnostic/icon-X.svg'} alt="" aria-hidden="true" className="size-5" />
      <span className="sr-only">{value}</span>
    </span>
  )
}

function chunk<T>(items: T[], size: number): T[][] {
  const result: T[][] = []
  for (let index = 0; index < items.length; index += size) result.push(items.slice(index, index + size))
  return result
}
